import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/pages/dashboard/_shared";
import { FileSpreadsheet, Plus, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NEXT_STATUS = { draft: "submitted", submitted: "certified", certified: "paid" };
const NEXT_LABEL = { draft: "Submit claim", submitted: "Mark certified", certified: "Mark paid" };

/**
 * Progress claims panel on the Job page (office/admin/QS). Claims are
 * built against the job's accepted quote lines — cumulative % complete
 * per line, retention held, GST, CCA-worded PDF.
 */
export function JobProgressClaims({ jobId }) {
  const [claims, setClaims] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get(`/jobs/${jobId}/claims`)
      .then((r) => setClaims(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };
  useEffect(load, [jobId]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/claims`);
      setEditing(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create claim");
    } finally {
      setCreating(false);
    }
  };

  const pdf = async (c) => {
    try {
      const res = await api.get(`/claims/${c.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `payment-claim-${c.claim_ref}.pdf`;
      a.click();
    } catch { toast.error("PDF failed"); }
  };

  const advance = async (c) => {
    const next = NEXT_STATUS[c.status];
    if (!next) return;
    try {
      await api.post(`/claims/${c.id}/status`, { status: next });
      toast.success(`${c.claim_ref} → ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete draft ${c.claim_ref}?`)) return;
    try {
      await api.delete(`/claims/${c.id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!loaded) return null;

  return (
    <section className="hard-card p-5" data-testid="job-claims-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag flex items-center gap-1.5">
          <FileSpreadsheet className="w-3 h-3" /> Progress claims
        </div>
        <Button size="sm" onClick={create} disabled={creating}
                className="h-8 rounded-sm bg-[#09090B] hover:bg-zinc-800 font-bold uppercase tracking-wider text-[10px]"
                data-testid="job-claim-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New claim
        </Button>
      </div>

      {claims.length === 0 ? (
        <div className="text-xs text-zinc-400 italic" data-testid="job-claims-empty">
          No claims yet. Claims are built from the job's accepted quote line items — hit New claim to raise PC-001.
        </div>
      ) : (
        <div className="space-y-1.5">
          {claims.map((c) => (
            <div key={c.id} className="flex items-center gap-2 p-2 border border-zinc-200 text-xs flex-wrap"
                 data-testid={`job-claim-row-${c.id}`}>
              <button type="button" onClick={() => setEditing(c)}
                      className="font-mono font-bold text-[#FF5F15] hover:underline"
                      data-testid={`job-claim-open-${c.id}`}>
                {c.claim_ref}
              </button>
              <span className="text-zinc-500">{c.claim_date}{c.period_label ? ` · ${c.period_label}` : ""}</span>
              <StatusBadge status={c.status} />
              <span className="ml-auto font-mono font-bold">{money(c.total)}</span>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-sm" onClick={() => pdf(c)}
                      title="Download PDF" data-testid={`job-claim-pdf-${c.id}`}>
                <Download className="w-3.5 h-3.5" />
              </Button>
              {NEXT_STATUS[c.status] && (
                <Button size="sm" variant="outline" onClick={() => advance(c)}
                        className="h-6 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[9px] px-2"
                        data-testid={`job-claim-advance-${c.id}`}>
                  {NEXT_LABEL[c.status]}
                </Button>
              )}
              {c.status === "draft" && (
                <button type="button" onClick={() => remove(c)} className="text-zinc-400 hover:text-red-600"
                        data-testid={`job-claim-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ClaimEditor claim={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </section>
  );
}

function ClaimEditor({ claim, onClose, onSaved }) {
  const [lines, setLines] = useState(claim.lines || []);
  const [retention, setRetention] = useState(claim.retention_pct ?? 5);
  const [period, setPeriod] = useState(claim.period_label || "");
  const [notes, setNotes] = useState(claim.notes || "");
  const [saving, setSaving] = useState(false);
  const readOnly = claim.status !== "draft";

  const setPct = (idx, v) => {
    setLines((arr) => arr.map((l) => (l.idx === idx ? { ...l, pct: v } : l)));
  };

  // Live preview totals (server recalcs authoritatively on save)
  const preview = (() => {
    let sub = 0;
    for (const l of lines) {
      const pct = Math.max(0, Math.min(100, parseFloat(l.pct) || 0));
      sub += (l.contract_amount * pct) / 100 - (l.contract_amount * (l.prev_pct || 0)) / 100;
    }
    const ret = (sub * (parseFloat(retention) || 0)) / 100;
    const net = sub - ret;
    const gst = net * 0.15;
    return { sub, ret, net, gst, total: net + gst };
  })();

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/claims/${claim.id}`, {
        lines: lines.map((l) => ({ idx: l.idx, pct: parseFloat(l.pct) || 0 })),
        retention_pct: parseFloat(retention) || 0,
        period_label: period.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success(`${claim.claim_ref} saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="claim-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold uppercase tracking-tight">
            Payment claim {claim.claim_ref} — {claim.job_title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Period</label>
            <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. July 2026"
                   disabled={readOnly} className="h-9 rounded-sm mt-1" data-testid="claim-period-input" />
          </div>
          <div>
            <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Retention %</label>
            <Input type="number" min="0" max="100" step="0.5" value={retention}
                   onChange={(e) => setRetention(e.target.value)} disabled={readOnly}
                   className="h-9 rounded-sm mt-1" data-testid="claim-retention-input" />
          </div>
        </div>

        {/* Lines */}
        <div className="mt-2">
          <div className="grid grid-cols-[1fr_80px_70px_90px_90px] gap-2 text-[9px] font-bold uppercase tracking-widest text-zinc-500 pb-1 border-b border-zinc-300">
            <span>Line</span><span className="text-right">Contract</span><span className="text-right">% to date</span>
            <span className="text-right">Prev claimed</span><span className="text-right">This claim</span>
          </div>
          <div className="space-y-1 mt-1">
            {lines.map((l) => {
              const pct = Math.max(0, Math.min(100, parseFloat(l.pct) || 0));
              const thisAmt = (l.contract_amount * pct) / 100 - (l.contract_amount * (l.prev_pct || 0)) / 100;
              return (
                <div key={l.idx} className="grid grid-cols-[1fr_80px_70px_90px_90px] gap-2 items-center text-xs py-1 border-b border-zinc-100"
                     data-testid={`claim-line-${l.idx}`}>
                  <span className="truncate font-medium">{l.description}</span>
                  <span className="text-right font-mono">{money(l.contract_amount)}</span>
                  <Input type="number" min={l.prev_pct || 0} max="100" step="1" value={l.pct}
                         onChange={(e) => setPct(l.idx, e.target.value)} disabled={readOnly}
                         className="h-7 rounded-sm text-right text-xs px-1"
                         data-testid={`claim-line-pct-${l.idx}`} />
                  <span className="text-right font-mono text-zinc-500">{money((l.contract_amount * (l.prev_pct || 0)) / 100)}</span>
                  <span className={`text-right font-mono font-bold ${thisAmt < 0 ? "text-red-600" : ""}`}>{money(thisAmt)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals */}
        <div className="mt-2 ml-auto w-64 space-y-1 text-xs" data-testid="claim-totals">
          <div className="flex justify-between"><span>This claim (excl GST)</span><span className="font-mono">{money(preview.sub)}</span></div>
          <div className="flex justify-between text-zinc-500"><span>Retention ({retention || 0}%)</span><span className="font-mono">-{money(preview.ret)}</span></div>
          <div className="flex justify-between"><span>Net claim</span><span className="font-mono">{money(preview.net)}</span></div>
          <div className="flex justify-between text-zinc-500"><span>GST 15%</span><span className="font-mono">{money(preview.gst)}</span></div>
          <div className="flex justify-between font-bold text-sm border-t-2 border-zinc-900 pt-1">
            <span>Total claimed</span><span className="font-mono" data-testid="claim-total-value">{money(preview.total)}</span>
          </div>
        </div>

        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={readOnly}
                  placeholder="Notes (shown on the PDF)" className="rounded-sm text-sm min-h-16"
                  data-testid="claim-notes-input" />

        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="claim-editor-close">
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={save} disabled={saving}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider"
                    data-testid="claim-editor-save">
              {saving ? "Saving…" : "Save claim"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
